import Alpine from "alpinejs";
import persist from "@alpinejs/persist";

Alpine.plugin(persist);

const GIFT_HANDLE = "tea-tumbler-gift";

const UPSELL_HANDLES = [
  "pineapple-tea-sleep-tea",
  "beauty-supplement-2",
  "cleanse-pineapple-tea-upsell",
  "skinny-sleeptox-tea-upsell-2",
]

const storefrontEndpoint = new URL(
  `https://${globalThis.Shopify.shop}/api/2025-07/graphql.json`,
);
async function graphql(query, variables = {}, { fragments = [], signal } = {}) {
  const { data, errors } = await fetch(storefrontEndpoint, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-shopify-storefront-access-token": "d36337bcf7ac168885c8ada549203bed",
    },
    body: JSON.stringify({
      query: [query, ...fragments].join("\n"),
      variables,
    }),
    signal,
  }).then((res) => res.json());
  if (errors) {
    for (const { message, ...cause } of errors) {
      console.error(new Error(message, { cause }));
    }
    throw new Error("Storefront error");
  }
  const userErrors = Object.values(data ?? {}).flatMap(
    (data) => data.userErrors ?? [],
  );
  if (userErrors.length) {
    for (const { message, ...cause } of userErrors) {
      console.error(new Error(message, { cause }));
    }
    throw new Error("Storefront user errors");
  }
  const warnings = Object.values(data ?? {}).flatMap(
    (data) => data.warnings ?? [],
  );
  if (warnings.length) {
    console.warn("Storefront warnings", warnings);
  }
  return data;
}

Alpine.magic("storefront", () => graphql);
Alpine.magic("money", () => (value, opts = {}) => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency:
      typeof value == "number"
        ? globalThis.Shopify.currency.active
        : value.currencyCode,
    ...opts,
  }).format(
    typeof value == "number"
      ? value
      : typeof value.amount == "string"
        ? Number.parseFloat(value.amount)
        : value.amount,
  );
});

const CartFragment = `
  fragment fullCart on Cart {
    cost {
      totalAmount {
        amount
        currencyCode
      }
    }
    totalQuantity
    lines(first: 10) {
      nodes {
        id
        quantity
        cost {
          amountPerQuantity {
            amount
            currencyCode
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            id
            name
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            image {
              altText
              width
              height
              url(transform: { maxWidth: 100 })
            }
            selectedOptions {
              name
              value
            }
            compareAtPrice {
              amount
              currencyCode
            }
            price {
              amount
              currencyCode
            }
            product {
              id
              title
              handle
              productType
              subscription: metafield(
                namespace: "custom"
                key: "subscription_product"
              ) {
                reference {
                  ... on Product {
                    id
                    title
                    variants(first: 5) {
                      nodes {
                        id
                        selectedOptions {
                          name
                          value
                        }
                      }
                    }
                    sellingPlanGroups(first: 1) {
                      nodes {
                        sellingPlans(first: 5) {
                          nodes {
                            id
                            name
                          }
                        }
                      }
                    }
                  }
                }
              }
              original: metafield(
                namespace: "custom"
                key: "original_product"
              ) {
                reference {
                  ... on Product {
                    id
                    title
                    variants(first: 5) {
                      nodes {
                        id
                        selectedOptions {
                          name
                          value
                        }
                      }
                    }
                  }
                }
              }
              sellingPlanGroups(first: 1) {
                nodes {
                  sellingPlans(first: 5) {
                    nodes {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

Alpine.store("cart", {
  open: false,
  id: decodeURIComponent(
    new Map(
      document.cookie.split(/;\s*/).map((cookie) => cookie.split("=", 2)),
    ).get("cart") ?? "",
  ),
  get fullId() {
    return `gid://shopify/Cart/${this.id}`;
  },
  lines: {
    nodes: [],
  },
  totalQuantity:
    Number.parseInt(document.querySelector("mini-cart").dataset.count) || 0,
  cost: {
    totalAmount: {
      amount: 0,
      currencyCode: globalThis.Shopify.currency.active,
    },
  },
  get totalCost() {
    return Number.parseFloat(this.cost.totalAmount.amount);
  },
  async init() {
    if (!this.id) {
      const { cartCreate } = await graphql(`
        mutation {
          cartCreate {
            cart {
              id
            }
            userErrors {
              field
              message
            }
            warnings {
              code
              message
            }
          }
        }
      `);
      const gid = new URL(cartCreate.cart.id);
      this.id = gid.pathname.split("/").at(-1) + gid.search;
      document.cookie = `cart=${encodeURIComponent(this.id)}`;
    }
    const result = await graphql(
      `
        query ($id: ID!) {
          cart(id: $id) {
            ...fullCart
          }
        }
      `,
      { id: this.fullId },
      { fragments: [CartFragment] },
    );
    Object.assign(this, result.cart);
  },
  async updateCart(...changes) {
    const update = changes.filter((change) => "id" in change);
    const add = changes.filter((change) => !("id" in change));
    const { cartLinesAdd } = await graphql(
      `
        mutation (
          $cartId: ID!
          $update: [CartLineUpdateInput!]!
          $add: [CartLineInput!]!
        ) {
          cartLinesUpdate(cartId: $cartId, lines: $update) {
            userErrors {
              field
              message
            }
          }
          cartLinesAdd(cartId: $cartId, lines: $add) {
            userErrors {
              field
              message
            }
            cart {
              ...fullCart
            }
          }
        }
      `,
      { cartId: this.fullId, update, add },
      { fragments: [CartFragment] },
    );

    const newCart = cartLinesAdd.cart;
    const cartTotal = Number(newCart.cost.totalAmount.amount) || 0;

    const giftCtx = Alpine.store("gift");
    const giftRemovals = update.flatMap((update) => {
      if (update.quantity != 0) return [];

      const product = this.lines.nodes.find((node) => node.id == update.id)
        ?.merchandise.product;
      return product?.productType.toLowerCase() == "gift"
        ? [product.handle]
        : [];
    });

    giftCtx.ignored = [...new Set([...giftCtx.ignored, ...giftRemovals])];

    const giftChanges = [];
    const gifts = [
      {
        ...giftCtx.product,
        requirement: giftCtx.requirement,
        handle: giftCtx.handle,
      },
    ];
    const giftsShouldHave =
      gifts.filter(
        (gift) =>
          !giftCtx.ignored.includes(gift.handle) &&
          gift.requirement < cartTotal &&
          newCart.lines.nodes.every(
            (line) => line.merchandise.product.handle != gift.handle,
          ),
      ) ?? [];
    giftChanges.push(
      ...giftsShouldHave.map((gift) => ({
        merchandiseId: gift.selectedOrFirstAvailableVariant.id,
        quantity: 1,
      })),
    );
    giftCtx.ignored = giftCtx.ignored.filter(
      (handle) =>
        gifts.find((gift) => gift.handle == handle)?.requirement < cartTotal,
    );

    const lineGiftsToRemove = newCart.lines.nodes.filter(
      (line) =>
        line.merchandise.product.productType == "Gift" &&
        gifts.find((gift) => line.merchandise.product.handle == gift.handle)
          ?.requirement > cartTotal,
    );

    giftChanges.push(
      ...lineGiftsToRemove.map((line) => ({ id: line.id, quantity: 0 })),
    );
    if (giftChanges.length) {
      return this.updateCart(...giftChanges);
    }

    Object.assign(this, newCart);
  },
  async addItemFromForm(event) {
    event.preventDefault();
    const formData = new FormData(event.target);

    let attributes = [];
    for (const [key, value] of formData) {
      const result = /^properties\[(.*)\]$/.exec(key);
      if (result) {
        attributes.push({
          value,
          key: result[1],
        });
      }
    }

    await this.updateCart({
      merchandiseId: `gid://shopify/ProductVariant/${formData.getAll("id").at(-1)}`,
      quantity: Number(formData.get("quantity")) || 1,
      sellingPlanId: formData.get("selling_plan")
        ? `gid://shopify/SellingPlan/${formData.get("selling_plan")}`
        : null,
      attributes,
    });

    this.open = true;
  },
  get canSubscribe() {
    return this.lines.nodes.filter((node) => getSellingPlans(node).length > 0);
  },
  get isSubscribed() {
    return this.lines.nodes.filter((node) => node.sellingPlanAllocation);
  },
  async bulkChangeSellingPlans(shouldSubscribe) {
    let changes = [];
    const { canSubscribe, isSubscribed } = this;
    if (shouldSubscribe) {
      const toSubscribe = canSubscribe.filter(
        (node) => !isSubscribed.includes(node),
      );
      changes = toSubscribe.flatMap((node) =>
        changeSellingPlan(node, getSellingPlans(node)[0].id),
      );
    } else {
      const toUnsubscribe = canSubscribe.filter((node) =>
        isSubscribed.includes(node),
      );
      changes = toUnsubscribe.flatMap((node) => changeSellingPlan(node, null));
    }
    await this.updateCart(...changes);
  },
});

function getSellingPlans(lineItem) {
  const product = lineItem.merchandise.product;
  const subscriptionPlans =
    product.subscription?.reference?.sellingPlanGroups?.nodes.flatMap((group) =>
      group.sellingPlans.nodes.map((plan) => ({
        ...plan,
        variantId: findSameVariant(
          product.subscription.reference.variants.nodes,
          lineItem.merchandise.selectedOptions,
        )?.id,
      })),
    );
  if (subscriptionPlans?.length) return subscriptionPlans;

  const selfPlans = product.sellingPlanGroups.nodes.flatMap((group) =>
    group.sellingPlans.nodes.map((plan) => ({
      ...plan,
      variantId: lineItem.merchandise.id,
    })),
  );

  return selfPlans;
}

function changeSellingPlan(lineItem, sellingPlanId) {
  const changes = [];
  const newPlan = getSellingPlans(lineItem).find(
    (plan) => plan.id == sellingPlanId,
  );
  if (newPlan?.variantId == lineItem.merchandise.id) {
    changes.push({
      id: lineItem.id,
      sellingPlanId: newPlan?.id,
    });
  } else {
    const merchandiseId =
      newPlan?.variantId ??
      findSameVariant(
        lineItem.merchandise.product.original?.reference.variants.nodes ?? [],
        lineItem.merchandise.selectedOptions,
      )?.id;
    changes.push(
      { id: lineItem.id, quantity: 0 },
      {
        quantity: lineItem.quantity,
        merchandiseId,
        sellingPlanId,
      },
    );
  }
  return changes;
}

function findSameVariant(variants, currentOptions) {
  const current = new Map(currentOptions.map((opt) => [opt.name, opt.value]));
  for (const variant of variants) {
    if (
      variant.selectedOptions.every((opt) => current.get(opt.name) == opt.value)
    ) {
      return variant;
    }
  }
}

Alpine.data("cartItem", () => ({
  get sellingPlans() {
    return getSellingPlans(this.item);
  },
  async changeSellingPlan(event) {
    const sellingPlanId = event.target.value || null; // set to null not empty string

    await this.$store.cart.updateCart(
      ...changeSellingPlan(this.item, sellingPlanId),
    );
  },
}));

Alpine.store("gift", {
  handle: GIFT_HANDLE,
  requirement: 79,
  product: null,
  ignored: Alpine.$persist([]),
  async init() {
    const { product } = await graphql(
      `
        query ($handle: String!) {
          product(handle: $handle) {
            title
            featuredImage {
              url(transform: { maxWidth: 40 })
              width
              height
              altText
            }
            selectedOrFirstAvailableVariant {
              id
            }
          }
        }
      `,
      { handle: this.handle },
    );
    this.product = product;

    console.log(product);
  },
  get progress() {
    const fraction = Math.min(
      1,
      Alpine.store("cart").totalCost / this.requirement,
    );
    return Math.round(fraction * 100);
  },
});

Alpine.data("upsells", () => ({
  products: [],
  async init() {
    this.products = await Promise.all(
      UPSELL_HANDLES.map((handle) =>
        graphql(
          `
            query getProduct($handle: String!) {
              product(handle: $handle) {
                id
                title
                handle
                availableForSale
                featuredImage {
                  url(transform: { maxWidth: 100 })
                  altText
                  width
                  height
                }
                variants(first: 5) {
                  nodes {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          `,
          { handle },
        ).then((res) => res.product),
      ),
    );
  },
  get filteredProducts() {
    const cart = Alpine.store("cart");
    return this.products
      .filter((prod) =>
        cart.lines.nodes.every(
          (node) => node.merchandise.product.id != prod.id,
        ),
      )
      .slice(0, 3);
  },
}));

const originalFetch = window.fetch;
window.fetch = function (...args) {
  const url = args[0];

  if (url && typeof url === "string" && url.includes("/cart/add.js")) {
    return originalFetch.apply(this, args).then((response) => {
      if (response.ok) {
        setTimeout(async () => {
          const cart = Alpine.store("cart");
          await cart.updateCart();
          cart.open = true;
        }, 0);
      }
      return response;
    });
  }

  return originalFetch.apply(this, args);
};

globalThis.Alpine = Alpine;
Alpine.start();

customElements.define(
  "mini-cart",
  class extends HTMLElement {
    connectedCallback() {
      Alpine.initTree(this.shadowRoot);
    }
    disconnectedCallback() {
      Alpine.destroyTree(this.shadowRoot);
    }
  },
);
